/**
 * PageHeader — navigation header with back button, Mark+wordmark, bell badge.
 * Design source: prototype.jsx:661-716.
 * Atom purity: imports only react + tokens + ./GlassPill + ./Mark.
 */
import { CSSProperties, ReactNode } from 'react';
import { G, P, S, T } from '../../styles/tokens';
import { GlassPill } from './GlassPill';
import { Mark } from './Mark';

export interface PageHeaderProps {
  title?: string;
  subtitle?: string;
  onBack?: () => void;
  openInbox?: () => void;
  unread?: number;
  dark?: boolean;
  right?: ReactNode;
}

function ChevLeft({ color = P.ink }: { color?: string }) {
  return (
    <svg width={11} height={18} viewBox="0 0 11 18" fill="none">
      <path
        d="M9 1L2 9l7 8"
        stroke={color}
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function PageHeader({
  title,
  subtitle,
  onBack,
  openInbox,
  unread = 0,
  dark = false,
  right,
}: PageHeaderProps) {
  const inkColor = dark ? '#ffffff' : P.ink;
  const secondaryColor = dark ? S.secondaryLabelDark : S.secondaryLabel;

  const outerStyle: CSSProperties = {
    padding: '8px 16px 0',
  };

  const rowStyle: CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    minHeight: 44,
    gap: 10,
  };

  // Wordmark style differs slightly when back button is shown (prototype:671)
  const wordmarkWithBack: CSSProperties = {
    fontFamily: T.fontBody,
    fontSize: 15,
    fontWeight: 600,
    color: dark ? 'rgba(255,255,255,0.9)' : S.secondaryLabel,
    letterSpacing: 0.1,
  };

  const wordmarkHome: CSSProperties = {
    font: T.headline,
    color: dark ? '#ffffff' : S.label,
  };

  return (
    <div style={outerStyle}>
      <div style={rowStyle}>
        {/* Left: optional back pill + Mark + wordmark */}
        {onBack ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <GlassPill
              onClick={onBack}
              variant={dark ? 'dark' : 'light'}
              size={44}
              style={{ padding: 0, width: 44, flexShrink: 0 }}
            >
              <ChevLeft color={dark ? '#ffffff' : P.ink} />
            </GlassPill>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, opacity: 0.9 }}>
              <Mark size={20} />
              <span style={wordmarkWithBack}>Awardy</span>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Mark size={28} />
            <span style={wordmarkHome}>Awardy</span>
          </div>
        )}

        {/* Right: custom slot or bell */}
        {right ?? (
          openInbox ? (
            // NOTE: the FTR-1114 coachmarks tour runs only on the home screen, which uses
            // HomeHeader (the data-coach="bell" anchor lives there). PageHeader is the
            // generic header for the other screens and carries no tour anchor.
            <div style={{ position: 'relative', flexShrink: 0 }}>
              {/* Native <button> with glass styles — avoids nested-interactive a11y violation. */}
              <button
                type="button"
                aria-label="Уведомления"
                onClick={openInbox}
                style={{
                  // G.light/dark provides the glass border — don't add border:'none' after
                  ...(dark ? G.dark : G.light),
                  width: 44,
                  height: 44,
                  borderRadius: 22,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  flexShrink: 0,
                }}
              >
                {/* Bell icon */}
                <svg width={20} height={20} viewBox="0 0 24 24" fill={inkColor}>
                  <path d="M12 22a2.5 2.5 0 002.45-2H9.55A2.5 2.5 0 0012 22zm7-5l-1.6-1.6c-.26-.26-.4-.6-.4-.97V11a5 5 0 00-4-4.9V5a1 1 0 10-2 0v1.1A5 5 0 007 11v3.43c0 .37-.14.71-.4.97L5 17v1h14v-1z" />
                </svg>
              </button>
              {unread > 0 && (
                <div
                  style={{
                    position: 'absolute',
                    top: 3,
                    right: 3,
                    minWidth: 18,
                    height: 18,
                    borderRadius: 9,
                    background: P.terracotta,
                    color: '#ffffff',
                    fontSize: 11,
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0 5px',
                    border: `2px solid ${dark ? P.ink : P.bg}`,
                    boxSizing: 'content-box' as CSSProperties['boxSizing'],
                    pointerEvents: 'none',
                  }}
                >
                  {unread}
                </div>
              )}
            </div>
          ) : (
            <div style={{ width: 44 }} />
          )
        )}
      </div>

      {/* Large title — serif, 34px, letterSpacing 0.4 (prototype:704-708) */}
      {title && (
        <div
          style={{
            fontFamily: T.fontDisplay,
            fontSize: 34,
            fontWeight: 700,
            lineHeight: '41px',
            letterSpacing: 0.4,
            color: inkColor,
            marginTop: 14,
          }}
        >
          {title}
        </div>
      )}
      {subtitle && (
        <div
          style={{
            font: T.footnote,
            color: secondaryColor,
            marginTop: 2,
          }}
        >
          {subtitle}
        </div>
      )}
    </div>
  );
}
